import { useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import {
  DeviceMobile,
  X,
  ShareNetwork,
  PlusSquare,
  DotsThreeVertical,
  Compass,
  Copy,
  Check,
  CalendarCheck,
  BellRinging,
  Cake,
  SunHorizon,
} from "@phosphor-icons/react";
import { selectTherapistId, selectClinicId } from "../../store/slices/authSlice";
import {
  shouldPromptForPwaInstall,
  snoozePwaInstallPrompt,
  markPwaInstallPromptNever,
  markPwaInstalled,
  isIOS,
  isInAppBrowser,
} from "../../utils/deviceUtils";
import { trackPwaInstallPrompt } from "../../services/telemetry";
import "./PwaInstallPrompt.css";

const SHOW_DELAY_MS = 5000;
const SITE_URL = "https://clinikit.co.il/dashboard";
const OPEN_EVENT = "clinikit:pwa-install-open";

/**
 * Open the install banner on demand (e.g. from the user menu), bypassing the
 * "later" / "do not show again" gating. No-op when the component isn't mounted.
 */
export const openPwaInstallPrompt = () => {
  try {
    window.dispatchEvent(new CustomEvent(OPEN_EVENT));
  } catch (e) {
    /* ignore */
  }
};

const BENEFITS = [
  { icon: CalendarCheck, text: "עדכונים על פגישות ושינויים ביומן" },
  { icon: BellRinging, text: "תזכורות למשימות שמחכות לכם" },
  { icon: Cake, text: "ימי הולדת של מטופלים" },
  { icon: SunHorizon, text: 'הודעת "בוקר טוב" עם סיכום היום שלכם' },
];

/**
 * PwaInstallPrompt — bottom banner shown to mobile users browsing CliniKit in a
 * regular browser tab (not the installed PWA). Nudges them to install so they
 * get push notifications.
 *
 * Cadence: shown on every visit (after a short delay) until the user picks
 * "do not show again" (localStorage, per therapist) or installs the app
 * (`appinstalled`). "Show me later" only snoozes it for the current tab session
 * (sessionStorage). See deviceUtils.
 *
 * Three variants by environment:
 *  - android: native `beforeinstallprompt` one-tap install (falls back to
 *    manual "browser menu → add to home screen" steps if the event never fires)
 *  - ios: Safari has no install API → step-by-step Share → Add to Home Screen
 *  - inapp: embedded webview (Instagram/Facebook/WhatsApp…) can't install →
 *    ask to open in Safari/Chrome, with a copy-link button
 *
 * Every interaction is counted in telemetry (`pwaInstallPrompt.*`) and surfaced
 * in the NiV "שימוש ומדדים" tab.
 */
function PwaInstallPrompt() {
  const therapistId = useSelector(selectTherapistId);
  const clinicId = useSelector(selectClinicId);

  const [visible, setVisible] = useState(false);
  const [stepsOpen, setStepsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [hasNativePrompt, setHasNativePrompt] = useState(false);
  const deferredPromptRef = useRef(null);

  const variant = isInAppBrowser() ? "inapp" : isIOS() ? "ios" : "android";

  // Capture the native install prompt (Chrome/Android/Samsung) as early as possible
  useEffect(() => {
    const handleBeforeInstall = (event) => {
      event.preventDefault();
      deferredPromptRef.current = event;
      setHasNativePrompt(true);
    };
    const handleInstalled = () => {
      markPwaInstalled();
      trackPwaInstallPrompt("installed");
      deferredPromptRef.current = null;
      setVisible(false);
    };
    window.addEventListener("beforeinstallprompt", handleBeforeInstall);
    window.addEventListener("appinstalled", handleInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
      window.removeEventListener("appinstalled", handleInstalled);
    };
  }, []);

  // Decide whether to show — only once auth resolved, after a short delay.
  useEffect(() => {
    if (!therapistId || !clinicId) return undefined;
    if (!shouldPromptForPwaInstall(therapistId)) return undefined;
    const timer = setTimeout(() => {
      trackPwaInstallPrompt("shown");
      setVisible(true);
    }, SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [therapistId, clinicId]);

  // Manual open from the user menu ("איך מתקינים את האפליקציה?")
  useEffect(() => {
    const handleOpen = () => {
      trackPwaInstallPrompt("shown");
      setStepsOpen(false);
      setVisible(true);
    };
    window.addEventListener(OPEN_EVENT, handleOpen);
    return () => window.removeEventListener(OPEN_EVENT, handleOpen);
  }, []);

  // "Show me later" (also the X button) — hide for this tab session only
  const handleLater = useCallback(() => {
    snoozePwaInstallPrompt(therapistId);
    trackPwaInstallPrompt("dismiss");
    setVisible(false);
  }, [therapistId]);

  // "Do not show again" — permanent for this therapist on this browser
  const handleNever = useCallback(() => {
    markPwaInstallPromptNever(therapistId);
    trackPwaInstallPrompt("never");
    setVisible(false);
  }, [therapistId]);

  const handleInstall = useCallback(async () => {
    const deferred = deferredPromptRef.current;
    if (variant === "android" && deferred) {
      trackPwaInstallPrompt("install");
      try {
        deferred.prompt();
        const choice = await deferred.userChoice;
        deferredPromptRef.current = null;
        setHasNativePrompt(false);
        if (choice?.outcome === "accepted") {
          markPwaInstalled();
          setVisible(false);
        } else {
          trackPwaInstallPrompt("declined");
          setVisible(false);
        }
      } catch (error) {
        console.error("[CK-1060] PWA native install prompt failed:", {
          errorCode: "CK-1060",
          errorMessage: error?.message,
          timestamp: new Date().toISOString(),
        });
        setStepsOpen(true);
      }
      return;
    }
    setStepsOpen((open) => {
      if (!open) trackPwaInstallPrompt("steps");
      return !open;
    });
  }, [variant]);

  const handleCopyLink = useCallback(async () => {
    trackPwaInstallPrompt("copyLink");
    try {
      await navigator.clipboard.writeText(SITE_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("[CK-1061] Copy CliniKit link failed:", {
        errorCode: "CK-1061",
        errorMessage: error?.message,
        timestamp: new Date().toISOString(),
      });
    }
  }, []);

  if (!visible) return null;

  const primaryLabel =
    variant === "inapp"
      ? stepsOpen
        ? "הסתר"
        : "איך פותחים בדפדפן?"
      : variant === "android" && hasNativePrompt
      ? "התקנה למסך הבית"
      : stepsOpen
      ? "הסתר הוראות"
      : "איך מתקינים?";

  return (
    <div className="pwa-install-banner" role="dialog" aria-label="התקנת CliniKit">
      <div className="pwa-install-card">
        <div className="pwa-install-header">
          <div className="pwa-install-icon">
            <DeviceMobile size={26} weight="duotone" />
          </div>
          <div className="pwa-install-text">
            <span className="pwa-install-eyebrow">התראות פוש</span>
            <strong>התקינו את CliniKit על המכשיר</strong>
            <span className="pwa-install-sub">
              אפליקציה מהירה במסך הבית, בלי חנות ובלי הורדה — וקבלו התראות גם
              כשהדפדפן סגור:
            </span>
          </div>
          <button
            type="button"
            className="pwa-install-dismiss"
            onClick={handleLater}
            aria-label="סגור"
          >
            <X size={18} weight="bold" />
          </button>
        </div>

        <ul className="pwa-install-benefits">
          {BENEFITS.map(({ icon: Icon, text }) => (
            <li key={text}>
              <Icon size={16} weight="fill" />
              <span>{text}</span>
            </li>
          ))}
        </ul>

        {stepsOpen && variant === "ios" && (
          <ol className="pwa-install-steps">
            <li>
              <ShareNetwork size={16} weight="fill" />
              <span>
                לחצו על כפתור <b>שיתוף</b> בסרגל ספארי (ריבוע עם חץ למעלה)
              </span>
            </li>
            <li>
              <PlusSquare size={16} weight="fill" />
              <span>
                גללו ובחרו <b>"הוסף למסך הבית"</b>
              </span>
            </li>
            <li>
              <Check size={16} weight="bold" />
              <span>
                אשרו <b>"הוסף"</b>, פתחו את CliniKit ממסך הבית והפעילו התראות
              </span>
            </li>
          </ol>
        )}

        {stepsOpen && variant === "android" && (
          <ol className="pwa-install-steps">
            <li>
              <DotsThreeVertical size={16} weight="bold" />
              <span>
                פתחו את <b>תפריט הדפדפן</b> (שלוש נקודות)
              </span>
            </li>
            <li>
              <PlusSquare size={16} weight="fill" />
              <span>
                בחרו <b>"התקנת אפליקציה"</b> או <b>"הוספה למסך הבית"</b>
              </span>
            </li>
            <li>
              <Check size={16} weight="bold" />
              <span>אשרו, פתחו את CliniKit ממסך הבית והפעילו התראות</span>
            </li>
          </ol>
        )}

        {stepsOpen && variant === "inapp" && (
          <div className="pwa-install-steps pwa-install-steps--inapp">
            <p>
              <Compass size={16} weight="fill" />
              <span>
                הדפדפן המובנה (אינסטגרם / פייסבוק / וואטסאפ) לא מאפשר התקנה.
                העתיקו את הקישור ופתחו אותו ב־Safari או ב־Chrome.
              </span>
            </p>
            <button
              type="button"
              className="pwa-install-copy"
              onClick={handleCopyLink}
            >
              {copied ? <Check size={15} weight="bold" /> : <Copy size={15} />}
              {copied ? "הקישור הועתק" : "העתקת קישור"}
            </button>
          </div>
        )}

        <div className="pwa-install-actions">
          <button
            type="button"
            className="pwa-install-primary"
            onClick={handleInstall}
          >
            {primaryLabel}
          </button>
          <button
            type="button"
            className="pwa-install-later"
            onClick={handleLater}
          >
            הזכירו לי אחר כך
          </button>
        </div>
        <button
          type="button"
          className="pwa-install-never"
          onClick={handleNever}
        >
          לא להציג שוב
        </button>
      </div>
    </div>
  );
}

export default PwaInstallPrompt;
